import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { initFirebase } from "@core/services/api/firebase";
import { formatFirebaseError } from "@core/services/api/formatFirebaseError";
import {
  updateUserEmail,
  UpdateUserEmailResponse,
} from "../utils/updateUserEmail";
import { updateUserPhone } from "../utils/updateUserPhone";
import { AccountWithPhoneFormData } from "../profile.types";
import { accountWithPhoneSchema } from "../profile.schemas";

export type UseAccountMutationArgs = {
  /** Callback when mutation succeeds */
  onSuccess?: (
    data: UpdateUserEmailResponse,
    variables: AccountWithPhoneFormData
  ) => void;
  /** Callback to dispatch user update (e.g., to context) */
  onUserUpdate?: (
    userData: Record<string, unknown> & { email: string; authToken?: string }
  ) => void;
  /** Current user email */
  currentEmail: string;
  /** Current user phone number */
  currentPhoneNumber?: string;
  /** Whether to reload page after success (default: true) */
  reloadOnSuccess?: boolean;
};

export type UseAccountMutationReturn = UseMutationResult<
  UpdateUserEmailResponse,
  Error,
  AccountWithPhoneFormData
> & {
  /** The schema for form validation */
  schema: typeof accountWithPhoneSchema;
  /** Default values for the form */
  defaultValues: AccountWithPhoneFormData;
};

/**
 * Hook for account email and phone update mutation
 * Handles Firebase re-authentication for email changes and phone number updates
 */
export function useAccountMutation({
  onSuccess,
  onUserUpdate,
  currentEmail,
  currentPhoneNumber,
  reloadOnSuccess = true,
}: UseAccountMutationArgs): UseAccountMutationReturn {
  const mutation = useMutation({
    mutationFn: async (data: AccountWithPhoneFormData) => {
      const emailChanged = data.email !== currentEmail;
      const phoneChanged = data.phoneNumber !== currentPhoneNumber;

      // If only phone changed, just update phone
      if (!emailChanged && phoneChanged) {
        const phoneResult = await updateUserPhone({
          phoneNumber: data.phoneNumber || "",
        });
        // Return compatible response format
        return {
          message: phoneResult.message,
          user_data: phoneResult.user_data,
          authToken: "",
        } as UpdateUserEmailResponse;
      }

      // If email changed (with or without phone change), update email first
      if (emailChanged) {
        const emailResult = await updateUserEmail({
          currentEmail,
          newEmail: data.email,
          password: data.password,
        });

        // If phone also changed, update it too
        if (phoneChanged) {
          await updateUserPhone({
            phoneNumber: data.phoneNumber || "",
          });
        }

        return emailResult;
      }

      // No changes - return current state
      return {
        message: "No changes",
        user_data: {},
        authToken: "",
      } as UpdateUserEmailResponse;
    },
    onError: (error: Error) => {
      throw new Error(formatFirebaseError(error.message));
    },
    onSuccess: async (data, variables) => {
      const emailChanged = variables.email !== currentEmail;

      // Dispatch user update if callback provided
      onUserUpdate?.({
        ...data.user_data,
        email: variables.email,
        ...(data.authToken ? { authToken: data.authToken } : {}),
      });

      // Force refresh from Firebase if email changed
      if (emailChanged) {
        const auth = await initFirebase();
        const user = auth.currentUser;
        await user?.reload();
      }

      // Call success callback
      onSuccess?.(data, variables);

      // Reload page to reflect email change (only if email changed)
      if (emailChanged && reloadOnSuccess) {
        window.location.reload();
      }
    },
  });

  // Default values for the form
  const defaultValues: AccountWithPhoneFormData = {
    email: currentEmail,
    phoneNumber: currentPhoneNumber || "",
    password: "",
  };

  return {
    ...mutation,
    schema: accountWithPhoneSchema,
    defaultValues,
  };
}

export default useAccountMutation;

